import React, { useState } from 'react';
import { X, Star, MessageSquare, ThumbsUp, Zap, CreditCard, Wallet, Gauge, Shield } from 'lucide-react';
import { FeedbackSubmission, FeedbackSource } from '@/types';
import './FeedbackModal.css';

interface FeedbackModalProps {
  isOpen: boolean;
  source: FeedbackSource;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (submission: FeedbackSubmission) => void;
  onClose: () => void;
}

// ── Star Rating ───────────────────────────────────────────────────────────────

interface StarRatingProps {
  value: number | undefined;
  onChange: (v: number) => void;
  size?: number;
}

const StarRating: React.FC<StarRatingProps> = ({ value, onChange, size = 22 }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="fm-stars" onMouseLeave={() => setHovered(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`fm-star ${(hovered || value || 0) >= n ? 'filled' : ''}`}
          onMouseEnter={() => setHovered(n)}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star size={size} />
        </button>
      ))}
    </div>
  );
};

// ── Contextual flags ─────────────────────────────────────────────────────────

const SCAN_FLAGS = [
  "Couldn't read the card clearly",
  'Wrong data was extracted',
  'Took too long',
  'QR code wasn\'t detected',
];

const QR_FLAGS = [
  "QR code didn't scan with other apps",
  'Design/appearance could be better',
  'Missing information in QR',
];

const GENERAL_FLAGS = [
  'App crashed or gave an error',
  'Hard to find features',
  'Missing a feature I need',
];

// ── Main component ────────────────────────────────────────────────────────────

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  source,
  isSubmitting,
  error,
  onSubmit,
  onClose,
}) => {
  const [overallRating, setOverallRating] = useState<number | undefined>();
  const [wouldRecommend, setWouldRecommend] = useState<boolean | undefined>();
  const [cardScanningRating, setCardScanningRating] = useState<number | undefined>();
  const [qrGenerationRating, setQrGenerationRating] = useState<number | undefined>();
  const [cardManagementRating, setCardManagementRating] = useState<number | undefined>();
  const [walletRating, setWalletRating] = useState<number | undefined>();
  const [appPerformanceRating, setAppPerformanceRating] = useState<number | undefined>();
  const [appReliabilityRating, setAppReliabilityRating] = useState<number | undefined>();
  const [selectedFlags, setSelectedFlags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [featureRequest, setFeatureRequest] = useState('');

  if (!isOpen) return null;

  const toggleFlag = (flag: string) => {
    setSelectedFlags((prev) =>
      prev.includes(flag) ? prev.filter((f) => f !== flag) : [...prev, flag]
    );
  };

  const showContextualFlags =
    (cardScanningRating !== undefined && cardScanningRating <= 3) ||
    (qrGenerationRating !== undefined && qrGenerationRating <= 3);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!overallRating) return;

    const submission: FeedbackSubmission = {
      overall_rating: overallRating,
      would_recommend: wouldRecommend,
      card_scanning_rating: cardScanningRating,
      qr_generation_rating: qrGenerationRating,
      card_management_rating: cardManagementRating,
      wallet_rating: walletRating,
      app_performance_rating: appPerformanceRating,
      app_reliability_rating: appReliabilityRating,
      contextual_flags: selectedFlags.length > 0 ? selectedFlags : undefined,
      comment: comment.trim() || undefined,
      feature_request: featureRequest.trim() || undefined,
      feedback_source: source,
    };

    onSubmit(submission);
  };

  return (
    <div className="fm-overlay" onClick={onClose}>
      <div className="fm-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="fm-header">
          <div className="fm-header-icon">
            <MessageSquare size={22} />
          </div>
          <div>
            <h2 className="fm-title">Share Your Feedback</h2>
            <p className="fm-subtitle">Help us improve — takes less than 2 minutes</p>
          </div>
          <button className="fm-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="fm-body">

          {/* ── Section 1: Overall ── */}
          <div className="fm-section">
            <p className="fm-section-label">
              Overall experience <span className="fm-required">*</span>
            </p>
            <StarRating value={overallRating} onChange={setOverallRating} size={28} />
          </div>

          {/* ── Would recommend ── */}
          <div className="fm-section fm-recommend-row">
            <p className="fm-section-label">Would you recommend this to a colleague?</p>
            <div className="fm-toggle-group">
              <button
                type="button"
                className={`fm-toggle ${wouldRecommend === true ? 'active-yes' : ''}`}
                onClick={() => setWouldRecommend(wouldRecommend === true ? undefined : true)}
              >
                <ThumbsUp size={15} /> Yes
              </button>
              <button
                type="button"
                className={`fm-toggle ${wouldRecommend === false ? 'active-no' : ''}`}
                onClick={() => setWouldRecommend(wouldRecommend === false ? undefined : false)}
              >
                <ThumbsUp size={15} style={{ transform: 'scaleY(-1)' }} /> No
              </button>
            </div>
          </div>

          {/* ── Section 2: Feature ratings ── */}
          <div className="fm-section">
            <p className="fm-section-label fm-section-label--optional">
              Feature ratings <span className="fm-optional">(optional)</span>
            </p>
            <div className="fm-feature-grid">
              <div className="fm-feature-row">
                <span className="fm-feature-label">
                  <CreditCard size={14} /> Card scanning
                </span>
                <StarRating value={cardScanningRating} onChange={setCardScanningRating} />
              </div>
              <div className="fm-feature-row">
                <span className="fm-feature-label">
                  <Zap size={14} /> QR generation
                </span>
                <StarRating value={qrGenerationRating} onChange={setQrGenerationRating} />
              </div>
              <div className="fm-feature-row">
                <span className="fm-feature-label">
                  <CreditCard size={14} /> Card management
                </span>
                <StarRating value={cardManagementRating} onChange={setCardManagementRating} />
              </div>
              <div className="fm-feature-row">
                <span className="fm-feature-label">
                  <Wallet size={14} /> Wallet
                </span>
                <StarRating value={walletRating} onChange={setWalletRating} />
              </div>
              <div className="fm-feature-row">
                <span className="fm-feature-label">
                  <Gauge size={14} /> Performance
                </span>
                <StarRating value={appPerformanceRating} onChange={setAppPerformanceRating} />
              </div>
              <div className="fm-feature-row">
                <span className="fm-feature-label">
                  <Shield size={14} /> Reliability
                </span>
                <StarRating value={appReliabilityRating} onChange={setAppReliabilityRating} />
              </div>
            </div>
          </div>

          {/* ── Section 3: Contextual flags (shown only if low rating on scan/QR) ── */}
          {showContextualFlags && (
            <div className="fm-section fm-flags-section">
              <p className="fm-section-label fm-section-label--optional">
                What went wrong? <span className="fm-optional">(select all that apply)</span>
              </p>
              <div className="fm-flags-group">
                {cardScanningRating !== undefined && cardScanningRating <= 3 &&
                  SCAN_FLAGS.map((flag) => (
                    <button
                      key={flag}
                      type="button"
                      className={`fm-flag ${selectedFlags.includes(flag) ? 'selected' : ''}`}
                      onClick={() => toggleFlag(flag)}
                    >
                      {flag}
                    </button>
                  ))}
                {qrGenerationRating !== undefined && qrGenerationRating <= 3 &&
                  QR_FLAGS.map((flag) => (
                    <button
                      key={flag}
                      type="button"
                      className={`fm-flag ${selectedFlags.includes(flag) ? 'selected' : ''}`}
                      onClick={() => toggleFlag(flag)}
                    >
                      {flag}
                    </button>
                  ))}
                {(overallRating !== undefined && overallRating <= 3) &&
                  GENERAL_FLAGS.map((flag) => (
                    <button
                      key={flag}
                      type="button"
                      className={`fm-flag ${selectedFlags.includes(flag) ? 'selected' : ''}`}
                      onClick={() => toggleFlag(flag)}
                    >
                      {flag}
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* ── Section 4: Free text ── */}
          <div className="fm-section">
            <label className="fm-section-label fm-section-label--optional" htmlFor="fm-comment">
              Anything else? <span className="fm-optional">(optional)</span>
            </label>
            <textarea
              id="fm-comment"
              className="fm-textarea"
              placeholder="Tell us what you think…"
              maxLength={1000}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
            <span className="fm-char-count">{comment.length}/1000</span>
          </div>

          <div className="fm-section">
            <label className="fm-section-label fm-section-label--optional" htmlFor="fm-feature">
              Feature request <span className="fm-optional">(optional)</span>
            </label>
            <textarea
              id="fm-feature"
              className="fm-textarea"
              placeholder="Is there something you'd like us to add?"
              maxLength={500}
              value={featureRequest}
              onChange={(e) => setFeatureRequest(e.target.value)}
              rows={2}
            />
            <span className="fm-char-count">{featureRequest.length}/500</span>
          </div>

          {/* ── Error ── */}
          {error && <p className="fm-error">{error}</p>}

          {/* ── Footer ── */}
          <div className="fm-footer">
            <button type="button" className="fm-btn-cancel" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="fm-btn-submit"
              disabled={!overallRating || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="fm-spinner" />
                  Submitting…
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
